// /admin/ops/findings/$id — finding detail with resolve action.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import {
  fetchFinding,
  opsKeys,
  reopenFinding,
  resolveFinding,
} from "@/lib/ops-queries";
import { severityColor, severityLabel } from "@/lib/ops-types";
import { useAdminMap, adminName } from "@/hooks/useAdminNames";
import { ApprovedActionsPanel, LifecycleBadge } from "@/components/ops/brokerRecon";
import { BROKER_MONEY_TASK_TYPES } from "@/lib/ops-types";
import {
  CaseControls,
  ProviderActionsPanel,
  ProviderTracePanel,
  traceQueryFor,
} from "@/components/ops/providerOps";

export const Route = createFileRoute("/_app/ops/findings/$id")({
  params: { parse: (p) => ({ id: parseInt(p.id, 10) }), stringify: (p) => ({ id: String(p.id) }) },
  component: FindingDetail,
});

function FindingDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const admins = useAdminMap();
  const q = useQuery({
    queryKey: opsKeys.finding(id),
    queryFn: () => fetchFinding(id),
  });

  const [notes, setNotes] = useState("");
  const resolve = useMutation({
    mutationFn: (n: string) => resolveFinding(id, n),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon"] });
      setNotes("");
    },
    meta: { successMessage: "Finding resolved." },
  });
  const reopen = useMutation({
    mutationFn: () => reopenFinding(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon"] });
    },
    meta: { successMessage: "Finding reopened." },
  });

  if (q.isLoading) return <Skeleton className="h-64" />;
  if (q.isError || !q.data) return <div className="text-red-500">Finding not found.</div>;
  const f = q.data;
  const resolved = !!f.resolved_at_unix_ms && f.resolved_at_unix_ms > 0;

  // Cross-link target: balance findings drill into a wallet flow view;
  // matching findings drill into a trade integrity view. Other entity
  // types just show the JSON.
  const drill = computeDrill(f);
  // SM-665: a provider finding opens its investigation trace.
  const provider = f.task_type === "provider_funding";
  const trace = provider ? traceQueryFor(f.details) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/ops/findings" className="text-sm text-muted hover:text-fg">← Back to inbox</Link>
        <button
          onClick={() => navigate({ to: "/ops/findings" })}
          className="text-xs text-muted hover:text-fg"
        >
          Close
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Badge color={severityColor(f.severity)}>{severityLabel(f.severity)}</Badge>
            <code className="font-mono text-sm">{f.finding_code}</code>
            <span className="text-sm text-muted">— {f.entity_id}</span>
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Field label="Task type" value={f.task_type} />
            <Field label="Detected count" value={f.detected_count} highlight={f.detected_count > 1} />
            <Field label="First seen" value={fmtRelative(new Date(f.first_detected_at_unix_ms).toISOString())} />
            <Field label="Last seen" value={fmtRelative(new Date(f.last_detected_at_unix_ms).toISOString())} />
            <Field label="First run" value={f.first_run_id} link={`/ops/runs/${f.first_run_id}`} />
            <Field label="Last run" value={f.last_run_id} link={`/ops/runs/${f.last_run_id}`} />
          </dl>

          {drill && (
            <div>
              <Link to={drill.path} params={drill.params} search={drill.search} className="text-sm underline">
                {drill.label} →
              </Link>
            </div>
          )}

          <div>
            <h3 className="text-xs uppercase tracking-wide text-muted">Details</h3>
            <pre className="mt-1 max-h-80 overflow-auto rounded border border-border bg-card p-3 text-xs">
              {JSON.stringify(f.details, null, 2)}
            </pre>
          </div>
        </CardBody>
      </Card>

      {BROKER_MONEY_TASK_TYPES.includes(f.task_type) ? (
        <Card>
          <CardHeader>
            <CardTitle>Case</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
              <Field label="Root broker" value={detail(f, K.root_broker_id)} link={brokerLink(detail(f, K.root_broker_id))} />
              <Field label="Broker" value={detail(f, K.broker_id)} link={brokerLink(detail(f, K.broker_id))} />
              <Field label="Broker account" value={detail(f, K.broker_account_id)} />
              <Field label="Currency" value={detail(f, K.currency)} />
              <Field label="Source operation" value={detail(f, K.source_op_id)} />
              <Field label="Operation type" value={detail(f, K.operation_type)} />
              <Field label="Rule" value={detail(f, K.rule)} />
              <Field
                label="Lifecycle state"
                value={detail(f, K.lifecycle_state) === "—" ? "—" : <LifecycleBadge state={detail(f, K.lifecycle_state)} />}
              />
            </dl>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-muted">Approved action</h3>
              <div className="mt-2">
                <ApprovedActionsPanel code={f.finding_code} details={f.details ?? {}} />
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* SM-665: every finding is a case — owner, state, approved action. */}
      <Card>
        <CardHeader>
          <CardTitle>Case</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <CaseControls key={`${f.finding_id}:${f.case_updated_at_unix_ms ?? 0}`} finding={f} />
          {provider ? (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-muted">Approved recovery</h3>
              <div className="mt-2">
                <ProviderActionsPanel code={f.finding_code} details={f.details ?? {}} />
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {trace ? (
        <Card>
          <CardHeader>
            <CardTitle>Provider trace</CardTitle>
          </CardHeader>
          <CardBody>
            <ProviderTracePanel query={trace} />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{resolved ? "Resolution" : "Resolve"}</CardTitle>
        </CardHeader>
        <CardBody>
          {resolved ? (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
                <Field label="Resolved at" value={fmtRelative(new Date(f.resolved_at_unix_ms!).toISOString())} />
                <Field label="Resolved by" value={adminName(admins, f.resolved_by)} />
              </dl>
              <div>
                <h3 className="text-xs uppercase tracking-wide text-muted">Notes</h3>
                <p className="mt-1 text-sm">{f.resolution_notes || <span className="text-muted">no notes</span>}</p>
              </div>
              <Button onClick={() => reopen.mutate()} disabled={reopen.isPending} variant="ghost">
                Re-open
              </Button>
              {reopen.isError && <p className="text-sm text-red-500">{(reopen.error as Error).message}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                className="w-full rounded border border-border bg-bg p-2 text-sm"
                rows={3}
                placeholder="Resolution notes (required, ≥5 chars). What was the root cause? Where's the follow-up ticket?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <Button
                onClick={() => resolve.mutate(notes.trim())}
                disabled={resolve.isPending || notes.trim().length < 5}
              >
                Resolve
              </Button>
              {resolve.isError && <p className="text-sm text-red-500">{(resolve.error as Error).message}</p>}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Field({ label, value, link, highlight }: { label: string; value: React.ReactNode; link?: string; highlight?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className={`mt-0.5 ${highlight ? "text-yellow-500 font-semibold" : ""}`}>
        {link ? (
          <Link to={link} className="hover:underline">{value}</Link>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

// computeDrill picks the right drill-down link for a finding. Balance
// findings on a flow entity → wallet flow chain view. Matching
// findings on a trade entity → trade integrity view. Others: no drill.
function computeDrill(f: ReturnType<typeof Object> extends never ? never : any): {
  path: string;
  params: Record<string, string>;
  search: Record<string, unknown>;
  label: string;
} | null {
  if (f.task_type === "balance" && f.entity_type === "flow" && f.details?.wallet_id) {
    return {
      path: "/ops/wallets/$walletId/flows",
      params: { walletId: String(f.details.wallet_id) },
      search: {
        field: String(f.details.field ?? "AVAILABLE"),
        break_flow_id: Number(f.details.flow_id ?? 0),
      },
      label: "View wallet flow chain",
    };
  }
  if (f.task_type === "matching" && f.details?.trade_id) {
    return {
      path: "/ops/trades/$tradeId",
      params: { tradeId: String(f.details.trade_id) },
      search: {},
      label: "View trade integrity",
    };
  }
  return null;
}

/** The details keys every broker code stamps (contracts ops/v1 codes_broker.go). */
const K = {
  root_broker_id: "root_broker_id",
  broker_id: "broker_id",
  broker_account_id: "broker_account_id",
  currency: "currency",
  source_op_id: "source_op_id",
  operation_type: "operation_type",
  rule: "rule",
  lifecycle_state: "lifecycle_state",
} as const;

/** A broker identifier from the details; "—" when the finding has none. */
function detail(f: { details?: Record<string, unknown> }, key: string): string {
  const v = f.details?.[key];
  return typeof v === "string" && v !== "" ? v : "—";
}

function brokerLink(id: string): string | undefined {
  return id === "—" ? undefined : `/ops/brokers/${encodeURIComponent(id)}`;
}
